import React, { useMemo } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";
import { IDropdownItem } from "../../dropdown/dropdownUtils";
import Dropdown from "../../dropdown/Dropdown";

// Types
interface MonthProps {
    value: number; // 0..11
    onChange: (monthIndex: number) => void;
    locale?: string; // default: browser
    ariaLabel?: string;
}

interface YearProps {
    value: number; // e.g., 2025
    onChange: (year: number) => void;
    minYear?: number; // inclusive
    maxYear?: number; // inclusive
    /** If neither minYear nor maxYear is provided, generates [value-window, value+window] */
    window?: number; // default 15
    ariaLabel?: string;
}

/** Small trigger chip that fits your dropdown trigger wrapper */
const TriggerChip: React.FC<{ text: string; ariaLabel?: string }> = ({ text, ariaLabel }) => {
    return (
        <div
            aria-label={ariaLabel}
            className="userful-mini-trigger"
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "4px 8px",
                borderRadius: 8,
                background: "#fff",
                lineHeight: 1.2,
                fontSize: 14,
                cursor: "pointer",
                userSelect: "none",
            }}
        >
            <span style={{ whiteSpace: "nowrap" }}>{text}</span>
            <FontAwesomeIcon icon={faChevronDown} style={{ opacity: 0.8 }} />
        </div>
    );
};

export const MonthDropdown: React.FC<MonthProps> = ({ value, onChange, locale, ariaLabel = "Choose month" }) => {
    const months: IDropdownItem[] = useMemo(() => {
        const base = new Date(2023, 0, 1);
        return Array.from({ length: 12 }, (_, m) => ({
            id: String(m),
            title: new Date(base.getFullYear(), m, 1).toLocaleString(locale, {
                month: "long",
            }),
        }));
    }, [locale]);

    const currentLabel = months[value]?.title ?? "";

    return (
        <Dropdown
            items={months}
            trigger={<TriggerChip text={currentLabel} ariaLabel={ariaLabel} />}
            onChange={(item) => onChange(parseInt(item.id, 10))}
        />
    );
};

export const YearDropdown: React.FC<YearProps> = ({
    value,
    onChange,
    minYear,
    maxYear,
    window = 15,
    ariaLabel = "Choose year",
}) => {
    const years: IDropdownItem[] = useMemo(() => {
        let from = minYear ?? value - window;
        let to = maxYear ?? value + window;
        if (minYear !== undefined && maxYear !== undefined && minYear > maxYear) {
            const tmp = from;
            from = to;
            to = tmp; // swap if inverted
        }
        const arr: IDropdownItem[] = [];
        for (let y = from; y <= to; y++) {
            arr.push({ id: String(y), title: String(y) });
        }
        return arr;
    }, [value, minYear, maxYear, window]);

    return (
        <Dropdown
            items={years}
            trigger={<TriggerChip text={String(value)} ariaLabel={ariaLabel} />}
            onChange={(item) => onChange(parseInt(item.id, 10))}
        />
    );
};
